import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster as SonnerToaster } from "sonner";
import LeadModalProvider from "./components/neptune/LeadModalContext";
import ScrollToTop from "./components/neptune/ScrollToTop";
import { ContentProvider } from "./context/ContentContext";
import { AuthProvider } from "./context/AuthContext";
import { MerchantAuthProvider } from "./context/MerchantAuthContext";
import Landing from "./pages/Landing";
import Solutions from "./pages/Solutions";
import Industries from "./pages/Industries";
import About from "./pages/About";
import Contact from "./pages/Contact";
import LegalPage from "./pages/LegalPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AuthCallback from "./pages/admin/AuthCallback";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminMerchants from "./pages/admin/AdminMerchants";
import AdminMerchantDetail from "./pages/admin/AdminMerchantDetail";
import AdminOnboardingLinks from "./pages/admin/AdminOnboardingLinks";
import AdminNereaAutonomy from "./pages/admin/AdminNereaAutonomy";
import AdminNereaRoster from "./pages/admin/AdminNereaRoster";
import AdminNereaShell from "./pages/admin/AdminNereaShell";
import AdminCrossCheck from "./pages/admin/AdminCrossCheck";
import AdminNereaUsage from "./pages/admin/AdminNereaUsage";
import AdminNereaUsageMerchant from "./pages/admin/AdminNereaUsageMerchant";
import AdminNereaConversations from "./pages/admin/AdminNereaConversations";
import AdminNereaConversationDetail from "./pages/admin/AdminNereaConversationDetail";
import AdminOpsInbox from "./pages/admin/AdminOpsInbox";
import AdminOpsPending from "./pages/admin/AdminOpsPending";
import AdminPipeline from "./pages/admin/AdminPipeline";
import AdminProspectDetail from "./pages/admin/AdminProspectDetail";
import AdminTeam from "./pages/admin/AdminTeam";
import AdminSystemSettings from "./pages/admin/AdminSystemSettings";
import AdminAutonomyRehearsal from "./pages/admin/AdminAutonomyRehearsal";
import AdminPayTechFAQ from "./pages/admin/AdminPayTechFAQ";
import AdminNeptunePlatformKnowledge from "./pages/admin/AdminNeptunePlatformKnowledge";
import AdminReassurance from "./pages/admin/AdminReassurance";
import AdminReReview from "./pages/admin/AdminReReview";
import AdminDocsManual from "./pages/admin/AdminDocsManual";
import AdminOnboardingSessions from "./pages/admin/AdminOnboardingSessions";
import AdminOnboardingSessionDetail from "./pages/admin/AdminOnboardingSessionDetail";
import AdminSolutions from "./pages/admin/AdminSolutions";
import AdminSolutionDetail from "./pages/admin/AdminSolutionDetail";
import PageEditor from "./pages/admin/PageEditor";
import LegalIndex from "./pages/admin/LegalIndex";
import MerchantLogin from "./pages/merchant/Login";
import MerchantSignup from "./pages/merchant/Signup";
import MerchantOnboardLanding from "./pages/merchant/OnboardLanding";
import MerchantResetPassword from "./pages/merchant/ResetPassword";
import AppShell from "./pages/merchant/AppShell";
import Dashboard from "./pages/merchant/Dashboard";
import OnboardingPage from "./pages/merchant/onboarding/OnboardingPage";
import FinancialsPage from "./pages/merchant/FinancialsPage";
import GatewayPage from "./pages/merchant/GatewayPage";
import IntegrationPage from "./pages/merchant/IntegrationPage";
import SettingsPage from "./pages/merchant/SettingsPage";
import MerchantProtected from "./pages/merchant/MerchantProtected";
import PublicNereaWidget from "./components/marketing/PublicNereaWidget";
import { useLocation } from "react-router-dom";

function MarketingWidget() {
  const { pathname } = useLocation();
  // Mount only on public marketing surfaces — not on the admin
  // back-office or merchant app shell.
  const onPublic =
    pathname === "/" ||
    pathname === "/solutions" ||
    pathname === "/industries" ||
    pathname === "/about" ||
    pathname === "/company" ||
    pathname === "/contact" ||
    pathname.startsWith("/legal/");
  if (!onPublic) return null;
  return <PublicNereaWidget />;
}

function App() {
  return (
    <div className="App dark">
      {/* Brief 3.13.4 (Feb 27 2026) — mount sonner Toaster at the root.
         Previously `toast.success/error/loading` calls compiled silently
         (no Toaster anywhere in the tree), which is why every admin
         action looked like "nothing happens" — every toast call was a
         no-op. Dark theme, top-right placement so it doesn't collide
         with sticky bottom bars in the merchant + admin shells. */}
      <SonnerToaster theme="dark" position="top-right" richColors
        toastOptions={{ duration: 5000 }} />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <MerchantAuthProvider>
            <ContentProvider>
              <LeadModalProvider>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/industries" element={<Industries />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/company" element={<About />} />
                  <Route path="/contact" element={<Contact />} />

                  {/* Merchant auth (Email + Password) — separate from /admin (Google) */}
                  <Route path="/login" element={<MerchantLogin />} />
                  <Route path="/signup" element={<MerchantSignup />} />
                  <Route path="/onboard/:token" element={<MerchantOnboardLanding />} />
                  <Route path="/reset-password" element={<MerchantResetPassword />} />
                  <Route
                    path="/app"
                    element={
                      <MerchantProtected>
                        <AppShell />
                      </MerchantProtected>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="onboarding" element={<OnboardingPage />} />
                    <Route path="onboarding/step/:stepNum" element={<OnboardingPage />} />
                    <Route path="financials" element={<FinancialsPage />} />
                    <Route path="gateway" element={<GatewayPage />} />
                    <Route path="integration" element={<IntegrationPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                  </Route>

                  {/* Legal — dynamic, not in nav, linked from footer. Legacy short URLs redirect. */}
                  <Route path="/legal/:slug" element={<LegalPage />} />
                  <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
                  <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
                  <Route path="/compliance" element={<Navigate to="/legal/aml-statement" replace />} />

                  {/* Admin (staff) */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/auth/callback" element={<AuthCallback />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboard />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="merchants" element={<AdminMerchants />} />
                    <Route path="merchants/:merchantId" element={<AdminMerchantDetail />} />
                    <Route path="merchants/:merchantId/cross-check" element={<AdminCrossCheck />} />
                    <Route path="onboarding-links" element={<AdminOnboardingLinks />} />
                    {/* Brief 3.5 PR 2 §1.4 — collapse 6 Nerea entries into one nav item.
                        Sub-shell wraps the 4 non-super_admin Nerea pages; the two
                        super_admin-only surfaces (Rehearsal, Reassurance) remain at
                        their existing /admin/ops/* URLs and are linked from the
                        sub-shell tab-bar. Zero URL changes / link rot. */}
                    <Route path="nerea" element={<AdminNereaShell />}>
                      <Route path="autonomy" element={<AdminNereaAutonomy />} />
                      <Route path="roster" element={<AdminNereaRoster />} />
                      <Route path="usage" element={<AdminNereaUsage />} />
                      <Route path="usage/merchants/:merchantId" element={<AdminNereaUsageMerchant />} />
                      <Route path="conversations" element={<AdminNereaConversations />} />
                      <Route path="conversations/:threadId" element={<AdminNereaConversationDetail />} />
                    </Route>
                    <Route path="ops/inbox" element={<AdminOpsInbox />} />
                    <Route path="ops/pending" element={<AdminOpsPending />} />
                    {/* Brief 3.5 PR 2 §1.4 — wrap the two super_admin-only Nerea surfaces
                        with AdminNereaShell so the sub-shell tab-bar stays visible when
                        the operator navigates to them from the sub-shell. Legacy URLs
                        /admin/ops/autonomy-rehearsal and /admin/ops/reassurance are
                        preserved (no link rot). */}
                    <Route path="ops/autonomy-rehearsal" element={<AdminNereaShell />}>
                      <Route index element={<AdminAutonomyRehearsal />} />
                    </Route>
                    <Route path="ops/reassurance" element={<AdminNereaShell />}>
                      <Route index element={<AdminReassurance />} />
                    </Route>
                    <Route path="ops/paytech-faq" element={<AdminPayTechFAQ />} />
                    <Route path="ops/neptune-platform-knowledge" element={<AdminNeptunePlatformKnowledge />} />
                    <Route path="compliance/re-review" element={<AdminReReview />} />
                    <Route path="docs/manual" element={<AdminDocsManual />} />
                    <Route path="docs/manual/:slug" element={<AdminDocsManual />} />
                    <Route path="onboarding-sessions" element={<AdminOnboardingSessions />} />
                    <Route path="onboarding-sessions/:sessionId" element={<AdminOnboardingSessionDetail />} />
                    <Route path="pipeline" element={<AdminPipeline />} />
                    <Route path="prospects/:prospectId" element={<AdminProspectDetail />} />
                    <Route path="team" element={<AdminTeam />} />
                    <Route path="system-settings" element={<AdminSystemSettings />} />
                    <Route path="solutions" element={<AdminSolutions />} />
                    <Route path="solutions/:solutionId" element={<AdminSolutionDetail />} />
                    <Route path="legal" element={<LegalIndex />} />
                    <Route path="pages/:pageKey" element={<PageEditor />} />
                  </Route>
                </Routes>
                <MarketingWidget />
              </LeadModalProvider>
            </ContentProvider>
          </MerchantAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
